
import React from 'react'
import { createRoot } from 'react-dom/client'
import thunk from 'redux-thunk'
import Raven from 'raven-js'
import { Switch, Redirect, Route } from 'react-router-dom'

import logException from './logs/index.js'

// Caching — service worker is being torn down. We call unregister() so any
// browser that still has a precache-based SW active will drop it on next visit
// and future deploys propagate immediately (no more N+1 visit model). Same
// pattern applied to frontend-gohire v1.
import { unregister as unregisterServiceWorker } from './registerServiceWorker.js'

import { createStore, combineReducers, applyMiddleware } from 'redux'
import { Provider } from 'react-redux'

import { createBrowserHistory } from 'history'

import {
  ConnectedRouter,
  routerMiddleware,
  connectRouter,
} from 'connected-react-router'

/**
 * Include here all the components and reducers
 *
 */
// Main CSS
import './main.css'

import 'bootstrap/dist/css/bootstrap.min.css'

// Get components
import Inbox from './components/inbox/index.js'
import InboxV2 from './components/inbox-v2/index.tsx'
import AIAssistants from './components/ai-assistant/index.tsx'
import ContactsV2 from './components/contacts/ContactsV2.js'
import CampaignsV2 from './components/campaign/CampaignsV2.js'
import CampaignViewRouter from './components/campaign/CampaignViewRouter.js'
import Profile from './components/profile/profile.js'
import UserProfile from './components/profile/UserProfile.js'
import BuildBot from './components/build-bot/BuildBotV2'
import Login from './components/login/index.js'
import AntdLogin from './components/login/AntdLogin.js'
import Templates from './components/templates/index.js'
import Users from './components/user-management/index.jsx'
import AccessControl from './components/access-control/index.jsx'
import IntentDictionary from './components/intent-dictionary/index.jsx'
import { withFeature } from './components/common/FeatureGate'
import { UpgradeModalHost } from './components/common/UpgradeModal'

import AccountSetup from './components/account-setup/index.js'
import Settings from './components/settings/index.js'
import Campaign from './components/campaign/index.js'
import CreateCampaignV3 from './components/campaign/CreateCampaignV3.js'
import ViewCampaign from './components/campaign/view-campaign.js'
import Groups from './components/groups/GroupsV2.js'
import CreateGroup from './components/groups/CreateGroupV2.js'
import ViewGroup from './components/groups/ViewGroupV2.js'
import ForgotPassword from './components/password/index.js'
import ResetPassword from './components/password/reset.js'
import Integrations from './components/integrations/index.js'
import MergeIntegrations from './components/merge-intergration/index.js'

import ModuleGroupV2 from './components/module-group/ModuleGroupV2.js'
import ModuleGroupView2 from './components/module-group/view2.js'
import SendMessage from './components/account-setup/send-message.js'
import Dashboard from './components/dashboard/index.js'
import ApplyByTextUnified from './components/apply-by-text/ApplyByTextUnified.js'
import Reporting from './components/reporting/index.js'
import ClientUsers from './components/client-users/index.js'
import InterviewInvitesUnified from './components/interview-invites/InterviewInvitesUnified.js'
import Store from './components/store/index.js'
import MessageAudit from './components/message-audit/index.js'
import UserAvailability from './components/availability/index.js'
import VonageNumbers from './components/numbers/index.js'
import Queues from './components/queues/index.js'
import EmailLog from './components/email-log/index.jsx'
import Sessions from './components/sessions/index.jsx'
import AiUsage from './components/ai-usage/index.jsx'

// Get all layouts
import DefaultLayout from './layouts/default/index.js'
import LoginLayout from './layouts/login/index.js'
import AntdLoginLayout from './layouts/login/AntdLoginLayout.js'
import RegisterLayout from './layouts/register/index.js'
import StoreLayout from './layouts/store/index.js'

// custom middlewares
import { isAuthenticated } from './utils/auth-helpers.js'
import { isAdmin } from './utils/is-admin.js'
import { setReduxStore } from './io'

// routes ACL
import RouteBaseComponent from './shared-components/route-base-component.js'

// Get all reducers
import reducers from './reducers/index.js'

// Needed for onTouchTap
/**
 * Commented out for React 16.8 upgrade
 */
// import injectTapEventPlugin from 'react-tap-event-plugin'

// Enable logging
// import LogRocket from 'logrocket'


if (process.env.NODE_ENV === 'production') {
  // LogRocket.init('s8y9m0/gohire')

  // Raven.config('https://48ab55362c3c43d1a7b9d55f6cacd141@sentry.io/232952')
  //   .install()
  //   .setDataCallback(function(data) {
  //     // add logrocket session
  //     data.extra.sessionURL = LogRocket.sessionURL
  //     return data
  //   })

  // error global event handler
  window.onerror = (error) => {
    logException(error)
    return false
  }
}

// http://stackoverflow.com/a/34015469/988941
/**
 * Commented out for React 16.8 upgrade
 */
// injectTapEventPlugin()

// Create a history of your choosing (we're using a browser history in this case)
const history = createBrowserHistory()

// Build the middleware for intercepting and dispatching navigation actions
const middleware = routerMiddleware(history)

// Add the reducer to your store on the `router` key
// Also apply our middleware for navigating
const store = createStore(
  combineReducers({
    ...reducers,
    router: connectRouter(history),
  }),
  applyMiddleware(thunk, middleware),
)

// Wire io.js's Dispatch to the real Redux dispatch so socket events
// (e.g. displayNotif → bell-icon update) work without depending on
// initRoom call sites that may not pass dispatch correctly.
setReduxStore(store)

if (window.Notification && Notification.permission !== 'denied') {
  Notification.requestPermission()
}

const root = createRoot(document.getElementById('root'));
import { ConfigProvider } from 'antd';
import { getTheme } from './theme-antd';
import { ThemeProvider, useTheme } from './ThemeContext';

const App = () => {
    const { isDarkMode } = useTheme();
    return (
        <ConfigProvider theme={getTheme(isDarkMode)}>
            <ConnectedRouter history={history}>
                <div className="container-fluid">
                  <div className="row app-container">
                    <Switch>
                      <DefaultLayout
                        path="/templates"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('templates.use', Templates),
                        )}
                      />
        
                      {/* <DefaultLayout
                        exact
                        path="/inbox"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Inbox,
                        )}
                      /> */}
                      <DefaultLayout
                        exact
                        path="/inbox"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('inbox', InboxV2),
                        )}
                      />
                     
                      <DefaultLayout
                        exact
                        path="/message-audit"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          MessageAudit,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/build-bot"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          BuildBot,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/queues"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Queues,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/email-log"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          EmailLog,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/sessions"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Sessions,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/ai-usage"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          AiUsage,
                        )}
                      />

                      <DefaultLayout
                        exact
                        path="/contacts"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('contacts', ContactsV2),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/interview-invites"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('interview-scheduling', InterviewInvitesUnified),
                        )}
                      />
                      <Route
                        exact
                        path="/interview-invites/integrations"
                        render={({ location }) => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'integrations');
                          return <Redirect to={`/interview-invites?${params.toString()}`} />;
                        }}
                      />
                      <DefaultLayout
                        exact
                        path="/groups"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('contacts', Groups),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/group/create"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('contacts', CreateGroup),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/groups/:groupId"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('contacts', ViewGroup),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/contacts/:contactId"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('contacts', UserProfile),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/contacts/:contactId/:convoId"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('contacts', UserProfile),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/companies"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Users,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/access-control"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          AccessControl,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/intent-dictionary"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          IntentDictionary,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/vonage-numbers"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          VonageNumbers
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/campaign"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('broadcasts', CampaignsV2),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/campaign/create"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('broadcasts', CreateCampaignV3),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/campaign/:campaignId"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('broadcasts', CampaignViewRouter),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/settings"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Settings,
                        )}
                      />
                      {/* <DefaultLayout
                        exact
                        path="/settings/profile"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Settings,
                        )}
                      /> */}
        
                       <DefaultLayout
                        exact
                        path="/integrations/v2"
                        component={RouteBaseComponent(
                          [isAuthenticated],
                          MergeIntegrations,
                        )}
                      />

                      <DefaultLayout
                        exact
                        path="/module-group"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          ModuleGroupV2,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/module-group/view/:modulegroupId"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          ModuleGroupView2,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/dashboard"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Dashboard,
                        )}
                      />
                        <DefaultLayout
                        exact
                        path="/ai-assistants"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('ai-assistant', AIAssistants),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/apply-by-text"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          withFeature('apply-by-text', ApplyByTextUnified),
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/reporting"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Reporting,
                        )}
                      />
                      <DefaultLayout
                        exact
                        path="/client-users"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          ClientUsers,
                        )}
                      />
                      <RegisterLayout
                        exact
                        path="/account-setup"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          AccountSetup,
                        )}
                      />
                      <RegisterLayout
                        exact
                        path="/account-setup/send-first-sms"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          SendMessage,
                        )}
                      />
                      <RegisterLayout
                        exact
                        path="/account-setup/integrate-calendar"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Integrations,
                        )}
                      />
                      <RegisterLayout
                        exact
                        path="/account-setup/integrate-calendar/:type"
                        component={RouteBaseComponent(
                          [isAuthenticated, isAdmin],
                          Integrations,
                        )}
                      />
                      <AntdLoginLayout
                        exact
                        path="/login"
                        component={RouteBaseComponent([isAuthenticated], AntdLogin)}
                      />
                      <AntdLoginLayout
                        exact
                        path="/forgot-password"
                        component={ForgotPassword}
                      />
                      <AntdLoginLayout
                        exact
                        path="/password/:type/:token"
                        component={ResetPassword}
                      />
                      <StoreLayout
                        exact
                        path="/store"
                        component={Store}
                      />
                      <Redirect to="/login" />
                    </Switch>
                  </div>
                </div>
            </ConnectedRouter>
        </ConfigProvider>
    );
};

root.render(
    <Provider store={store}>
        <ThemeProvider>
            <App />
            <UpgradeModalHost />
        </ThemeProvider>
    </Provider>
);

unregisterServiceWorker()
